import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NAV_ITEMS } from "@/app/navigation";

const STORAGE_KEY = "hippo.sidebarCollapsed";

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");

  function toggle() {
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      return next;
    });
  }

  return (
    <aside
      className={[
        "hidden md:flex shrink-0 flex-col border-r border-border bg-surface/60 backdrop-blur transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-64",
      ].join(" ")}
    >
      <Link
        to="/"
        title={collapsed ? "Hippo — Home" : undefined}
        className={[
          "flex items-center h-16 border-b border-border transition-colors hover:bg-surface-2",
          collapsed ? "justify-center px-0" : "gap-3 px-5",
        ].join(" ")}
      >
        <div className="grid place-items-center h-9 w-9 shrink-0 rounded-xl bg-primary text-primary-fg font-display font-bold shadow-glow">
          H
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-display font-semibold text-text">Hippo</div>
            <div className="text-xs text-muted">Game Design Studio</div>
          </div>
        )}
      </Link>

      <nav className={["flex-1 py-4 space-y-1", collapsed ? "px-2" : "px-3"].join(" ")}>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              [
                "group relative flex items-center rounded-lg py-2.5 text-sm font-medium transition-colors",
                collapsed ? "justify-center px-0" : "gap-3 px-3",
                isActive ? "text-text" : "text-muted hover:text-text hover:bg-surface-2",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="sidebar-active"
                    className="absolute inset-0 rounded-lg bg-surface-2 ring-1 ring-border"
                    transition={{ type: "spring", stiffness: 500, damping: 40 }}
                  />
                )}
                <item.icon className="relative z-10 h-[18px] w-[18px] shrink-0" />
                {!collapsed && <span className="relative z-10 truncate">{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <button
          onClick={toggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={[
            "flex w-full items-center rounded-lg py-2 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-text",
            collapsed ? "justify-center px-0" : "gap-3 px-3",
          ].join(" ")}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" />
          ) : (
            <>
              <PanelLeftClose className="h-[18px] w-[18px] shrink-0" />
              <span>Collapse</span>
            </>
          )}
        </button>
        {!collapsed && (
          <div className="px-3 pt-2 text-[11px] text-muted">v0.1.0 · Phase 0</div>
        )}
      </div>
    </aside>
  );
}
